<script setup>
import { useAuth } from "@/composables/useAuth";
import MiniStatisticsCard from "@/quote/Cards/MiniStatisticsCard.vue";
import GradientLineChart from "@/quote/Charts/GradientLineChart.vue";
import CategoriesList from "./components/CategoriesList.vue";
import ProximasCitas from "./citas/components/ProximasCitas.vue";


import { ref, computed, onMounted } from "vue";
import api from "../api/axios"
import { destinosDisponibles, paisesDestinos } from "@/utils/destinos";
const { user, loadUser, hasRole } = useAuth();

const topDestinations = ref([]);
const monthlyData = ref([]);
const topUsers = ref([]);
const stats = ref({
  today: 0,
  week: 0,
  month: 0,
  total: 0
});

const fetchStats = async () => {
  try {
    const res = await api.get("/quotes/stats");
    stats.value = res.data;
  } catch (error) {
    console.error(error);
  }
};




const fetchTopDestinations = async () => {
  try {
    const res = await api.get("/quotes/top-destinations");
    topDestinations.value = res.data;
  } catch (error) {
    console.error(error);
  }
};

const fetchMonthly = async () => {
  try {
    const res = await api.get("/quotes/monthly");
    monthlyData.value = res.data;
  } catch (error) {
    console.error(error);
  }
};

const fetchTopUsers = async () => {
  try {
    const res = await api.get("/quotes/top-users");
    topUsers.value = res.data;
  } catch (error) {
    console.error(error);
  }
};

onMounted(async () => {
  if (!user.value) {
    await loadUser();
  }

  await fetchStats();
  await fetchTopDestinations();
  await fetchMonthly();
  // El backend restringe /quotes/top-users a admin/supervisor (403 para el
  // resto); se evita la llamada de una vez ya que el widget tampoco se
  // renderiza para otros roles (ver v-if más abajo).
  if (hasRole("admin") || hasRole("supervisor")) {
    await fetchTopUsers();
  }

  console.log("👤 Usuario:", user.value);
});

const chartData = computed(() => ({
  labels: [
    "Ene", "Feb", "Mar", "Abr", "May", "Jun",
    "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
  ],
  datasets: [
    {
      label: "Cotizaciones Mensuales",
      data: monthlyData.value.map(m => m.total),
    },
  ],
}));

const nombresMeses = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const chartDescription = computed(() => {
  const mesActual = new Date().getMonth(); // 0 = enero
  const actual = monthlyData.value[mesActual]?.total ?? 0;
  const anterior = monthlyData.value[mesActual - 1]?.total ?? 0;
  const nombreAnterior = nombresMeses[mesActual - 1];

  if (mesActual === 0 || anterior === 0) {
    return `<span class='font-weight-bold'>${actual.toLocaleString()} cotizaciones</span> este mes`;
  }

  const variacion = ((actual - anterior) / anterior) * 100;
  const porcentaje = Math.abs(variacion).toFixed(1);

  if (variacion >= 0) {
    return `<i class='fa fa-arrow-up text-success'></i>
      <span class='font-weight-bold'>${porcentaje}% más</span> que el mes pasado (${nombreAnterior})`;
  }

  return `<i class='fa fa-arrow-down text-danger'></i>
    <span class='font-weight-bold'>${porcentaje}% menos</span> que el mes pasado (${nombreAnterior})`;
});

const nombreDestino = (codigo) =>
  destinosDisponibles.find(d => d.value === codigo)?.label ?? codigo;

const banderaDestino = (codigo) => {
  const iso = paisesDestinos[codigo];
  return iso ? `https://flagcdn.com/w40/${iso}.png` : null;
};

const valorPromedio = (valor) =>
  Math.round(Number(valor)).toLocaleString("es-CO");
</script>
<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-lg-3 col-md-6 col-12">
            <mini-statistics-card title="Cotizaciones hoy" :value="stats.today" :icon="{
              component: 'ni ni-watch-time',
              background: 'bg-gradient-primary',
              shape: 'rounded-circle',
            }" />
          </div>
          <div class="col-lg-3 col-md-6 col-12">
            <mini-statistics-card title="Cotizaciones Semanales" :value="stats.week" :icon="{
              component: 'ni ni-calendar-grid-58',
              background: 'bg-gradient-danger',
              shape: 'rounded-circle',
            }" />
          </div>
          <div class="col-lg-3 col-md-6 col-12">
            <mini-statistics-card title="Cotizaciones Mensuales" :value="stats.month" :icon="{
              component: 'ni ni-chart-bar-32',
              background: 'bg-gradient-success',
              shape: 'rounded-circle',
            }" />
          </div>
          <div class="col-lg-3 col-md-6 col-12">
            <mini-statistics-card title="Cotizaciones Totales" :value="stats.total" :icon="{
              component: 'ni ni-trophy',
              background: 'bg-gradient-warning',
              shape: 'rounded-circle',
            }" />
          </div>
    </div>
    <div class="row">
      <div class="col-12 mb-4 chart-col">
        <!-- line chart -->
        <gradient-line-chart v-if="monthlyData.length" id="chart-line" title="Cotizaciones"
          :description="chartDescription" :chart="chartData" />
      </div>
      <!-- <div class="col-lg-5">
        <carousel />
      </div> -->
    </div>
    <div v-if="hasRole('asesor_lider') || hasRole('asesor_lider_corporativo') || hasRole('admin')" class="row mt-4">
      <div class="col-12">
        <proximas-citas />
      </div>
    </div>
    <div class="row mt-4 bottom-row">
          <div :class="(hasRole('admin') || hasRole('supervisor')) ? 'col-lg-7 mb-lg-0 mb-4' : 'col-lg-12 mb-4'">
            <div class="card">
              <div class="p-3 pb-0 card-header">
                <div class="d-flex justify-content-between">
                  <h6 class="mb-2">Top Destinos</h6>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table align-items-center">
                  <tbody>
                    <tr v-for="(dest, index) in topDestinations" :key="index">
                      <td class="w-30">
                        <div class="px-2 py-1 d-flex align-items-center">
                          <img v-if="banderaDestino(dest.destination)" :src="banderaDestino(dest.destination)"
                            :alt="`Bandera de ${nombreDestino(dest.destination)}`"
                            class="avatar avatar-sm rounded-circle" style="object-fit: cover;" />
                          <span v-else class="avatar avatar-sm rounded-circle d-flex align-items-center justify-content-center bg-gradient-info">
                            <i class="ni ni-world text-white"></i>
                          </span>
                          <div class="ms-4">
                            <p class="mb-0 text-xs font-weight-bold">
                              Destino:
                            </p>
                            <h6 class="mb-0 text-sm">{{ nombreDestino(dest.destination) }}</h6>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div class="text-center">
                          <p class="mb-0 text-xs font-weight-bold">Cantidad:</p>
                          <h6 class="mb-0 text-sm">{{ dest.cantidad }}</h6>
                        </div>
                      </td>
                      <td>
                        <div class="text-center">
                          <p class="mb-0 text-xs font-weight-bold">Valor Promedio:</p>
                          <h6 class="mb-0 text-sm">
                            ${{ valorPromedio(dest.valor_promedio) }}
                          </h6>
                        </div>
                      </td>

                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div v-if="hasRole('admin') || hasRole('supervisor')" class="col-lg-5">
            <categories-list :categories="topUsers.map(u => ({
              label: u.name,
              description: `${u.cantidad} cotizaciones`
            }))" />
          </div>
    </div>
  </div>


</template>

<style scoped>
/* Altura fija para la gráfica: Chart.js necesita un contenedor con
   tamaño definido (responsive + maintainAspectRatio:false). Se mantiene
   igual en todos los tamaños de pantalla para evitar layouts frágiles. */
.chart-col :deep(.chart) {
  position: relative;
  height: 280px;
}

.chart-col :deep(.chart-canvas) {
  max-height: 100%;
}

/* Las listas de la fila inferior pueden crecer mucho (muchos destinos o
   asesores); se limita su alto y se da scroll interno en vez de alargar
   la página indefinidamente. Funciona igual en cualquier pantalla. */
.bottom-row :deep(.card-body),
.bottom-row :deep(.table-responsive) {
  max-height: 420px;
  overflow-y: auto;
}
</style>
